import type { CompanyProcedureDto, CompanyStandardDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant } from '../../lib/useTenant';
import { Button, ErrorText, Select } from '../../ui';
import { ProcedureTaskCard, standardValue } from './ProcedureTaskCard';
import { ProcedureTriggers } from './ProcedureTriggers';
import { ProcedureExecutors } from './Executions';

const ACTION: Record<string, string> = { crea: 'crea', modifica: 'modifica', chiude: 'chiude' };

/** Uno standard della procedura, con «Fissa il valore» per un responsabile quando è dell'azienda o di prassi. */
function Standard({ s }: { s: CompanyStandardDto }) {
  const { tenant, t, isManager } = useTenant();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(s.valore === null ? '' : String(s.valore));
  const [unit, setUnit] = useState(s.unita ?? '');
  const [cmp, setCmp] = useState<string>(s.confronto ?? '');
  const save = useMutation({
    mutationFn: () => {
      const v = value.trim().replace(',', '.');
      return api(t(`/company-standards/${s.id}`), { method: 'PATCH', body: { valore: v !== '' && !Number.isNaN(Number(v)) ? Number(v) : value.trim(), unita: unit.trim() || null, confronto: cmp || null } });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  return (
    <li className="text-xs text-ink/65">
      {s.nome}: <span className={s.da_fissare ? 'text-amber-700' : 'text-ink/80'}>{standardValue(s)}</span>
      <span className="text-ink/45"> · {s.misura}</span>
      {s.fissato_da && <span className="text-ink/45"> · fissato da {s.fissato_da}</span>}
      {s.dai_dati?.come === 'verifica' && <span className="text-ink/45"> · si misura dai dati{s.dai_dati.controllo_id ? ', con il suo controllo di gestione' : ''}</span>}
      {s.dai_dati?.come === 'indicatore' && <span className="text-ink/45"> · si misura con un indicatore</span>}
      {isManager && s.fissabile && !editing && (
        <button type="button" className="ml-2 text-accent hover:underline" onClick={() => setEditing(true)}>
          {s.da_fissare ? 'Fissa il valore' : 'Cambia il valore'}
        </button>
      )}
      {editing && (
        <span className="mt-1 flex flex-wrap items-center gap-2">
          <Select value={cmp} onChange={(e) => setCmp(e.target.value)}>
            <option value="">senza confronto</option>
            <option value="al_massimo">al massimo</option>
            <option value="almeno">almeno</option>
            <option value="uguale">uguale a</option>
          </Select>
          <input className="w-28 rounded border border-black/15 px-2 py-1 text-xs" placeholder="Valore" value={value} onChange={(e) => setValue(e.target.value)} />
          <input className="w-28 rounded border border-black/15 px-2 py-1 text-xs" placeholder="Unità (giorni, %…)" value={unit} onChange={(e) => setUnit(e.target.value)} />
          <Button className="px-2 py-1 text-xs" disabled={!value.trim() || save.isPending} onClick={() => save.mutate()}>
            Salva
          </Button>
          <button type="button" className="text-ink/50 hover:text-ink" onClick={() => setEditing(false)}>
            Lascia stare
          </button>
        </span>
      )}
      {save.error && <ErrorText>{errorMessage(save.error)}</ErrorText>}
    </li>
  );
}

/** «È arrivato…»: apre a mano il compito di una procedura che parte da un evento esterno. */
function Arrived({ p }: { p: CompanyProcedureDto }) {
  const { tenant, t } = useTenant();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [evento, setEvento] = useState(p.avvio.esterni[0]?.evento ?? '');
  const [dato, setDato] = useState('');
  const send = useMutation({
    mutationFn: () => api(t('/procedure-tasks/arrived'), { body: { procedura_id: p.id, evento, ...(dato.trim() ? { dato: dato.trim() } : {}) } }),
    onSuccess: () => {
      setOpen(false);
      setDato('');
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  if (!open)
    return (
      <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setOpen(true)}>
        È arrivato…
      </Button>
    );
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {p.avvio.esterni.length > 1 ? (
        <Select value={evento} onChange={(e) => setEvento(e.target.value)}>
          {p.avvio.esterni.map((x) => (
            <option key={x.evento} value={x.evento}>
              {x.evento}
            </option>
          ))}
        </Select>
      ) : (
        <span className="text-xs text-ink/70">{evento}</span>
      )}
      {p.per && <input className="w-48 rounded border border-black/15 px-2 py-1 text-xs" placeholder={`Quale ${p.per.replace(/_/g, ' ')} (facoltativo)`} value={dato} onChange={(e) => setDato(e.target.value)} />}
      <Button className="px-2 py-1 text-xs" disabled={send.isPending} onClick={() => send.mutate()}>
        Apri il compito
      </Button>
      <button type="button" className="text-xs text-ink/50 hover:text-ink" onClick={() => setOpen(false)}>
        Lascia stare
      </button>
      {send.error && <ErrorText>{errorMessage(send.error)}</ErrorText>}
    </div>
  );
}

/**
 * Il lavoro di una procedura nel gestionale (ADR 0215 tappa 4): da cosa parte, su quali dati agisce, chi approva, i
 * programmi che la possono svolgere, gli standard (con «Fissa il valore»); per una procedura attiva i compiti aperti e
 * «È arrivato…» se parte da un evento esterno.
 */
export function ProcedureWork({ p }: { p: CompanyProcedureDto }) {
  const starts = [
    p.avvio.tempo,
    ...p.avvio.esterni.map((e) => `a ogni ${e.evento}`),
    ...p.avvio.dopo.map((d) => `dopo «${d.procedura}»${d.se ? `, ${d.se}` : ''}`),
  ].filter(Boolean);
  return (
    <div className="mt-1 space-y-1">
      <ProcedureTriggers procedure={p} />
      <ProcedureExecutors procedure={p} />
      {starts.length > 0 && (
        <p className="text-xs text-ink/60">
          Parte: {starts.join(' · ')}
          {p.avvio.entro && <> · entro {p.avvio.entro}</>}
        </p>
      )}
      {p.scrive.length > 0 && <p className="text-xs text-ink/60">Dati: {p.scrive.map((w) => `${ACTION[w.azione]} ${w.entita}`).join(', ')}{p.legge.length > 0 && <> · legge {p.legge.join(', ')}</>}</p>}
      {(p.approva || p.sistemi.length > 0) && (
        <p className="text-xs text-ink/60">
          {p.approva && <>Approva: {p.approva}</>}
          {p.approva && p.sistemi.length > 0 && ' · '}
          {p.sistemi.length > 0 && (p.lo_puo_svolgere.length ? <>Lo può svolgere: {p.lo_puo_svolgere.join(', ')}</> : <>Di solito con un programma di {p.sistemi.map((s) => s.replace(/_/g, ' ')).join(', ')}</>)}
        </p>
      )}
      {p.standard.length > 0 && (
        <ul className="space-y-0.5">
          {p.standard.map((s) => (
            <Standard key={s.id} s={s} />
          ))}
        </ul>
      )}
      {p.attiva && (
        <div className="space-y-1.5 pt-1">
          {p.compiti_aperti.map((task) => (
            <ProcedureTaskCard key={task.id} task={task} showProcedure={false} />
          ))}
          {p.avvio.esterni.length > 0 && <Arrived p={p} />}
        </div>
      )}
    </div>
  );
}
